import React, {useEffect, useState} from 'react';
import './dashboard.scss';
import {gql} from 'apollo-boost';
import Welcome from './Welcome/Welcome';
import Accounts from './Account/Accounts';
import Loading from '../Loading/Loading';
import Button from '../Form/Button/Button'
import {useQuery} from "@apollo/react-hooks";
import {usePolling} from '../../utils/hooks';
import {print} from 'graphql/language/printer';
import withFeatureFlags from '../../components/FeatureFlags/withFeatureFlags';
import FatcaPopup from '../../components/FatcaPopup/FatcaPopup';
import Modal from '../Modal/Modal';

import {useCurrentUser} from '../../contexts/CurrentUserProvider';
import {useLogger} from '../../contexts/Logger';
import {MaturityBanner} from "../MaturityInstructionForm/DashboardMaturityBanner";
import {productKeys, productTypes} from "../../utils/productHelpers";
import {convertProductTermToMonths, sortArrayByObjectProperty} from "../../utils/helpers";
import {Feedback} from "../Feedback/Feedback";
import {submitFeedback} from "../Feedback/FeedbackServiceClient";

/** @import {DashboardProps, CustomerAndAccountsResponse, Account} from './DashboardTypes' */
/** @import {QueryResult} from '@apollo/react-common' */

const GET_CUSTOMER_AND_ACCOUNTS = gql`
    query GetCustomerAndAccounts($id : ID!){
        customer(id:$id) {
            accounts {
                accountNumber
                balance
                availableBalance
                id
                linkedAccountNumber
                linkedAccountSortCode
                interestPaid           
                product {
                    productType
                    interestRate
                    name
                    issueNumber
					maxWithdrawalLimit
                    lowerInterestRateAmount
                    stepUpAmount
                    interestRateTiers {
                      minBalance
                      maxBalance
                      rate
                    }
                    productTerm {
                        value
                        unit
                    }
                    code
                    rateChanges {
                        rate
                        aerRate
                        issue
                        scheduledFor
                        reason
                        rateNotificationAt
                    }
                }
                sortCode
                accountStatus
                fundingEndDate
                fundingWindowMinutesLeft
                transactions {
                  reference
                  transactionId
                  amount
                  updatedBalance
                  bookingDate
                  counterpartAccountNumber
                  counterpartSortCode
                  action
                  interestPeriodFrom
                  interestPeriodTo
                }
				pendingTransactions {
                  pendingTransactionId
                  counterpartAccountNumber
                  counterpartSortCode
                  counterpartAccountType
                  toLinkedAccount
                  amount
                  withdrawalDate
                  state
				  closeAccount
				  allowClosure
                }
				transferAccounts {
                  id
                  accountNumber
				  sortCode
                  balance
                  availableBalance
                  product {
                    productType
                    interestRate
                    name
                    maxWithdrawalLimit
                    lowerInterestRateAmount
                    stepUpAmount
                    interestRateTiers {
                      minBalance
                      maxBalance
                      rate
                    }
                    productTerm {
                        value
                        unit
                      }
                  }
				}
				maturity {
                  maturityInstructionWindowStatus
                  maturityInstruction
                  maturityWindowCloseDate
                  maturityWindowCloseTime
                  maturityDate
                  destinationAccountId
                  rolloverAccountProductId
                  availableRolloverRates {
                    productId
                    rate
                  }
                }
                notice {
                	noticePeriod
					noticePeriodUnit
                }
            }	
			firstName
            middleName
            id
            lastName
            mobileNumber
            applicationStatus
            showFatcaPopup
            newAccounts {
              productId
              qualify
              productName
              productRate
			  productAerRate
			  productType
            }
            products {
              id
              name
              rate
              rate
		      aerRate
		      issueNumber
              rateChangeNotification
              code
              type
              summaryBoxFileName
              productTermsFileName
              productTerm {
                value
                unit
              }
            }
            pendingAccounts {
              productId
            }
            isaAllowance {
                taxYear
                totalAllowance
                usedAllowance
                remainingAllowance
                holdAllowance
            }
        }
    }  `;

function NoAccountNotification(/** @type {{message: string;}} */ props) {
    return (<div className="dashboard-welcome__subtitle">
        {props.message}
    </div>);
};

function AdditionalAccountCreatedModal(/** @type {{closePopup: () => void;}} */ props) {
    return (
        <Modal className="modal modal__fixed-height" hideClose={true}>
            <h4 className="modal__title">Please bear with us</h4>
            <p className="modal__paragraph">Your new account may take a little while to appear in your dashboard - if
                you don't see it immediately, try refreshing the page in about five minutes.</p>
            <div className="modal__spacer" />
            <Button size='med' label="Got it"
                onClick={props.closePopup} />
        </Modal>
    )
};

function AccountCreating(/** @type {{customerId: string; onAccountCreated: (account: CustomerAndAccountsResponse) => void;}} */ props) {
    const [startPolling, setStartPolling] = useState(true);
    const currentUser = useCurrentUser();

    usePolling(async () => {
        if (!startPolling || !currentUser)
            return;

        try {
            const accessToken = currentUser.accessToken;

            // had to use fetch instead of the useQuery hook as it can only be used at root component level
            const result = await fetch('/graphql', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    query: print(GET_CUSTOMER_AND_ACCOUNTS),
                    variables: { id: props.customerId }
                }),
            });

            if (result.status === 200) {
                /** @type {{data: CustomerAndAccountsResponse}} */
                const resultBody = await result.json();
                if (resultBody.data.customer.accounts && resultBody.data.customer.accounts.length !== 0) {
                    setStartPolling(false);
                    if (props.onAccountCreated) {
                        props.onAccountCreated(resultBody.data);
                    }
                } else {
                }
            }
        } catch {
        }
    }, [startPolling], 5000);

    return <div className="dashboard-welcome__notification">
        We’re busy loading your account
        <div>
            <div className="lds-default">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
    </div>;
};

function Dashboard(/** @type {DashboardProps} */ { toAccount, featureEnabled }) {

    const [lazyLoadedCustomer, setLazyLoadedCustomer] = useState(/** @type {CustomerAndAccountsResponse | null} */ (null));
    const currentUser = useCurrentUser();
    const logger = useLogger();

	/** @type {QueryResult<CustomerAndAccountsResponse>} */
    const { loading, data, error } = useQuery(GET_CUSTOMER_AND_ACCOUNTS, {
        variables: { id: currentUser ? currentUser.customerId : undefined },
        skip: !currentUser || !currentUser.customerId
    });

    const [showFatcaPopup, setShowFatcaPopup] = useState(true);
    const [showAdditionalAccountPopup, setShowAdditionalAccountPopup] = useState(false);

    useEffect(() => {
        const qs = window.location.search.replace(/\?/g, '').split('&');
        const hasComeFromSecondaryAppForm = qs.filter(v => v.match(/^applicationAccepted=/));
        if (hasComeFromSecondaryAppForm && hasComeFromSecondaryAppForm.length === 1) {
            const productKey = qs.filter(v => v.match(/^productKey=/));
            productKey && productKey.length === 1 && sessionStorage.setItem(`app-accepted-${productKey[0].split('=')[1]}`, 'true');
            setShowAdditionalAccountPopup(true);
        }
    }, []);

    if (error) {
        logger.error('Failed to load customer data for dashboard', {}, error);
    }

    const onAccountCreated = (/** @type {CustomerAndAccountsResponse} */ loadedCustomer) => {
        setLazyLoadedCustomer(loadedCustomer);
    };

    if (loading || !currentUser || !currentUser.customerId || !data) return (
        <Loading message="We're loading your dashboard..." />
    );

    const customerData = lazyLoadedCustomer === null ? data : lazyLoadedCustomer;

    const sortCustomerAccounts = (/** @type {Account[]} */ accounts) => {
        const inactiveAccounts = accounts.filter(acc => acc.accountStatus === "Closed");
        const activeAccounts = accounts.filter(acc => acc.accountStatus !== "Closed");

        const frsActiveAccountsIWOpen = sortArrayByObjectProperty(
            activeAccounts.filter(acc => acc.maturity && acc.maturity.maturityInstructionWindowStatus === "InstructionOpen"),
            ['maturity', 'maturityWindowCloseDate'],
            Date.parse
        ).sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));

        const fisaActiveAccountsIWOpen = sortArrayByObjectProperty(
            activeAccounts.filter(acc => acc.maturity && acc.maturity.maturityInstructionWindowStatus === "InstructionOpen" && acc.product.productType === productTypes.FixedRateCashISA),
            ['maturity', 'maturityWindowCloseDate'],
            Date.parse
        ).sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));
        
        const ofsActiveAccounts = activeAccounts.filter(acc => acc.product.productType === productTypes.EasyAccess);
        const nsActiveAccounts = activeAccounts.filter(acc => acc.product.productType === productTypes.Notice);
        const frsActiveAccountsIWNotOpen = activeAccounts.filter(acc => acc.product.productType === productTypes.FixedTerm && acc.maturity && acc.maturity.maturityInstructionWindowStatus !== "InstructionOpen")
            .sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));

        const fisaActiveAccountsIWNotOpen = activeAccounts.filter(acc => acc.product.productType === productTypes.FixedRateCashISA && acc.maturity && acc.maturity.maturityInstructionWindowStatus !== "InstructionOpen")
            .sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));

        const ofsInactiveAccounts = inactiveAccounts.filter(acc => acc.product.productType === productTypes.EasyAccess);
        const nsInactiveAccounts = inactiveAccounts.filter(acc => acc.product.productType === productTypes.Notice);
        const frsInactiveAccounts = inactiveAccounts.filter(acc => acc.product.productType === productTypes.FixedTerm)
            .sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));
        const fisaInactiveAccounts = inactiveAccounts.filter(acc => acc.product.productType === productTypes.FixedRateCashISA)
            .sort((a, b) => convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm));
        
        const rest = activeAccounts.filter(acc => acc.product.productType !== productTypes.EasyAccess && acc.product.productType !== productTypes.Notice 
                        && acc.product.productType !== productTypes.FixedTerm && acc.product.productType !== productTypes.FixedRateCashISA);

        return [
            ...frsActiveAccountsIWOpen,
            ...fisaActiveAccountsIWOpen,
            ...ofsActiveAccounts,
            ...nsActiveAccounts,
            ...frsActiveAccountsIWNotOpen,
            ...fisaActiveAccountsIWNotOpen,
            ...ofsInactiveAccounts,
            ...nsInactiveAccounts,
            ...frsInactiveAccounts,
            ...fisaInactiveAccounts,
            ...rest
        ];
    }
    
    const closeAdditionalAccountPopup = () => {
        setShowAdditionalAccountPopup(false);
        window.history.replaceState({}, document.title, "/");
    };

    const frsNtb = data.customer.products.find(product => product.code === productKeys.FixedRateSaver);
    const frsNtbRate = frsNtb ? frsNtb.rate : undefined;
    const maturingAccounts = data.customer.accounts.filter(a => (a.maturity && a.maturity.maturityInstructionWindowStatus === "InstructionOpen" && !a.maturity.maturityInstruction))
    const rollingOverAccounts = data.customer.accounts.filter(a => (a.maturity && a.maturity.maturityInstruction.includes("Rollover") && a.accountStatus !== "Closed"));
    const rateChangeslist = data.customer.accounts.filter(account => account.accountStatus !== "ClosureRequested" && account.accountStatus !== "Closed").map(account => account.product.rateChanges || []).flat();
    // First condition
    const firstCondition = (maturingAccounts.length > 0 || rollingOverAccounts.length > 0) &&
        (rateChangeslist.length === 0);

    // Second condition
    const secondCondition = (maturingAccounts.length === 0) && (rollingOverAccounts.length === 0) &&
        (rateChangeslist.length > 0);

    // Third condition
    const thirdCondition = (maturingAccounts.length > 0 || rollingOverAccounts.length > 0) &&
        (rateChangeslist.length > 0);
    return (
        <div>
            {showAdditionalAccountPopup &&
                <AdditionalAccountCreatedModal closePopup={() => closeAdditionalAccountPopup()} />}
            {featureEnabled('EnableFatcaPopup') && customerData.customer.showFatcaPopup && showFatcaPopup &&
                <FatcaPopup closePopup={() => setShowFatcaPopup(false)} />}
            <Welcome name={customerData.customer.firstName} data={customerData} />
            {maturingAccounts.length > 0 &&
                <MaturityBanner maturingAccounts={maturingAccounts} ntbInterestRate={frsNtbRate} />
            }
            {(customerData.customer.applicationStatus === "under_review" || customerData.customer.applicationStatus === "awaiting_decision") &&
                <NoAccountNotification
                    message="We're reviewing your application and will be in touch shortly by email." />
            }

            {customerData.customer.applicationStatus === "approved_no_account" &&
                <AccountCreating onAccountCreated={onAccountCreated} customerId={currentUser.customerId} />
            }
            {customerData.customer.applicationStatus == "approved" &&
                <Accounts
                    accounts={sortCustomerAccounts(customerData.customer.accounts)}
                    pendingAccounts={customerData.customer.pendingAccounts}
                    customer={customerData.customer}
                    toAccount={toAccount}
                />
            }
            {firstCondition && (
                <p className={"maturity3__aer-info aer-info"}>
                    * AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest
                    was paid and compounded once a year. The AER is intended to be an indicative rate to help you compare the return on different savings products.
                </p>
            )}

            {secondCondition && (
                <div>
                    <p className={"maturity3__aer-info aer-info"}>
                        * AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest was paid and compounded once a year. The AER is intended to be an indicative rate to help you compare the return on different savings products.
                    </p>
                    <p className={"maturity3__aer-info aer-info"}>
                        ^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.
                    </p>
                </div>
            )}

            {thirdCondition && (
                <div>
                    <p className={"maturity3__aer-info aer-info"}>
                        * AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest was paid and compounded once a year. The AER is intended to be an indicative rate to help you compare the return on different savings products.
                    </p>
                    <p className={"maturity3__aer-info aer-info"}>
                        ^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.
                    </p>
                </div>
            )}
            <Feedback
                submitFeedback={(/** @type {string} */ rating, /** @type {string} */ comments) => {
                    submitFeedback(rating, comments);
                }}
                themeName={"monochrome"}
                enabled={featureEnabled('EnableDashboardFeedbackForm')}
            />
        </div>
    );
};

export default withFeatureFlags(Dashboard);
